import { FunctionalComponent, h } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { ViolationsProvider } from 'contexts/use-violations';
import { Skeleton } from 'components/skeleton/skeleton';
import { About } from 'components/about/about';
import { Login } from 'components/login/login';
import { ForgotPassword } from 'components/login/forgot-password/forgot-password';
import { Recovery } from 'components/login/recovery/recovery';
import { LstUpgrade } from 'components/live-site-translations/lst-upgrade';
import { AddonsAndSettings } from 'components/addons-and-settings/addons-and-settings';
import { ManageSkeleton } from 'components/manage/manage-skeleton/manage-skeleton';
import { ReportView } from 'components/report/report';
import { HowItWorksPage } from 'components/how-it-works/how-it-works-page';
import { InitialPageSections } from './initial-page-sections';
import { InitialPageLst } from './initial-page-lst';
import { RemediationRuleViews, Views, ViewsList } from './types';

const ManageInterfaceLazy = lazy(() =>
  import('../manage-interface/manage-interface').then((m) => ({ default: m.ManageInterface })),
) as unknown as FunctionalComponent<{ view: Views }>;

const createManageView = (view: Views) => () => (
  <ViolationsProvider>
    <Suspense key="manage" fallback={<ManageSkeleton withWCAGSummary />}>
      <ManageInterfaceLazy view={view} />
    </Suspense>
  </ViolationsProvider>
);

const createRemediationsListView = (view: Views) => () => (
  <Suspense key="manage" fallback={<ManageSkeleton />}>
    <ManageInterfaceLazy view={view} />
  </Suspense>
);

const remediationRuleViewComponents = Object.values(RemediationRuleViews).reduce(
  (components, view) => ({ ...components, [view]: createRemediationsListView(view) }),
  {} as Partial<Record<Views, () => JSX.Element>>,
);

const VIEW_COMPONENTS: Partial<Record<Views, () => JSX.Element>> = {
  ...remediationRuleViewComponents,
  [ViewsList.SkeletonPage]: () => <Skeleton />,
  [ViewsList.InitialPage]: () => <InitialPageSections />,
  [ViewsList.InitialPageLst]: () => <InitialPageLst />,
  [ViewsList.ReportPage]: () => <ReportView />,
  [ViewsList.AboutPage]: () => <About />,
  [ViewsList.HowItWorksPage]: () => <HowItWorksPage />,
  [ViewsList.Login]: () => <Login />,
  [ViewsList.ForgotPassword]: () => <ForgotPassword />,
  [ViewsList.Recovery]: () => <Recovery />,
  [ViewsList.LstUpgrade]: () => <LstUpgrade />,
  [ViewsList.AddonsAndSettings]: () => <AddonsAndSettings />,
  [ViewsList.Manage]: createManageView(ViewsList.Manage),
  [ViewsList.RemediationsList]: createRemediationsListView(ViewsList.RemediationsList),
};

interface InitialPageViewProps {
  view: Views;
}

export const InitialPageView: FunctionalComponent<InitialPageViewProps> = ({ view }) => {
  const Component = VIEW_COMPONENTS[view];
  return Component ? <Component /> : null;
};
